
import { useQuery } from "@tanstack/react-query";
import { getAboutContent } from "../lib/contentful";
import Layout from "../components/Layout";
import { Skeleton } from "@/components/ui/skeleton";
import ReactMarkdown from 'react-markdown';

const About = () => {
  const { data: aboutContent, isLoading, error } = useQuery({
    queryKey: ['aboutContent'],
    queryFn: getAboutContent,
  });

  if (error) {
    console.error("Error loading about content:", error);
  }

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <section className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-medium text-center mb-12">About Me</h1>
          
          <div className="flex flex-col md:flex-row gap-10">
            <div className="w-full md:w-2/5">
              <Skeleton className="h-[400px] w-full rounded-lg" />
            </div>
            
            <div className="w-full md:w-3/5 space-y-6">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-4/5" />
              
              <div className="pt-4">
                <Skeleton className="h-8 w-40 mb-3" />
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-8 w-24 rounded-full" />
                  ))}
                </div>
              </div>
              
              <div className="pt-4">
                <Skeleton className="h-8 w-40 mb-3" />
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-8 w-24 rounded-full" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-medium text-center mb-12">About Me</h1>
        
        <div className="flex flex-col md:flex-row gap-10 items-start">
          <div className="w-full md:w-2/5 md:sticky md:top-24">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-primary/10 rounded-lg blur opacity-30"></div>
              <div className="relative rounded-lg overflow-hidden">
                <img 
                  src={aboutContent?.photo} 
                  alt="Yuri Lobo" 
                  className="w-full h-auto object-cover aspect-[3/4]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          
          <div className="w-full md:w-3/5 space-y-6">
            <div className="markdown-content space-y-4">
              {aboutContent?.bio.map((paragraph, index) => (
                <div key={index} className="leading-relaxed">
                  <ReactMarkdown
                    components={{
                      a: ({ node, ...props }) => (
                        <a
                          {...props}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline font-medium"
                        />
                      ),
                      p: ({ node, ...props }) => (
                        <p {...props} className="mb-4 text-[15px] leading-relaxed font-roboto" />
                      ),
                      strong: ({ node, ...props }) => (
                        <span {...props} className="font-semibold" />
                      ),
                      em: ({ node, ...props }) => (
                        <span {...props} className="italic" />
                      ),
                      h1: ({ node, ...props }) => (
                        <h1 {...props} className="text-2xl font-bold my-4" />
                      ),
                      h2: ({ node, ...props }) => (
                        <h2 {...props} className="text-xl font-bold my-3" />
                      ),
                      h3: ({ node, ...props }) => (
                        <h3 {...props} className="text-lg font-bold my-2" />
                      ),
                      ul: ({ node, ...props }) => (
                        <ul {...props} className="list-disc ml-6 my-4" />
                      ),
                      ol: ({ node, ...props }) => (
                        <ol {...props} className="list-decimal ml-6 my-4" />
                      ),
                      li: ({ node, ...props }) => (
                        <li {...props} className="mb-1 text-[15px] leading-relaxed font-roboto" />
                      ),
                      br: () => <br className="my-2" />,
                      u: ({ node, ...props }) => (
                        <span {...props} className="underline" />
                      )
                    }}
                  >
                    {paragraph}
                  </ReactMarkdown>
                </div>
              ))}
            </div>
            
            <div className="pt-4">
              <h2 className="text-xl font-medium mb-3">Skills</h2>
              <div className="flex flex-wrap gap-2">
                {aboutContent?.skills.map((skill) => (
                  <span 
                    key={skill} 
                    className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm font-roboto"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="pt-4">
              <h2 className="text-xl font-medium mb-3">Languages</h2>
              <div className="flex flex-wrap gap-2">
                {aboutContent?.languages.map((language) => (
                  <span 
                    key={language} 
                    className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-roboto"
                  >
                    {language}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
